/* Account Settings — change password (for all logged-in users).
 * Mounted at /account/settings. Sidebar visible.
 */
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, KeyRound, User } from "lucide-react";
import PasswordRulesHint from "@/components/PasswordRulesHint";
import { validateNewPassword } from "@/lib/password";

export default function AccountSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validateNewPassword(form.next, form.confirm);
    if (v) { setErr(v); return; }
    if (form.current === form.next) {
      setErr("New password must be different from the current password.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password.");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 p-4 md:p-8" data-testid="account-settings-page">
      <div>
        <div className="overline text-teal-700">Account</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-600">Manage your FlowQA account preferences and security.</p>
      </div>

      <Card className="border-slate-200 p-6" data-testid="account-profile-card">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-slate-100 p-2.5 text-slate-700"><User className="size-5" /></div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">Profile</div>
            <p className="mt-1 text-sm text-slate-600">
              {user?.name} · {user?.email} · <span className="capitalize">{(user?.role || "").replace("_", " ")}</span>
              {user?.company ? <> · {user.company}</> : null}
            </p>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-6" data-testid="account-change-password-card">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><KeyRound className="size-5" /></div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold text-slate-900">Change password</div>
            <p className="text-sm text-slate-600">Choose a new password you have not used before.</p>
          </div>
        </div>

        <form className="mt-6 grid max-w-md gap-4" onSubmit={submit}>
          <div>
            <Label>Current password</Label>
            <Input
              type="password" autoComplete="current-password"
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              data-testid="as-current-input"
              required
            />
          </div>
          <div>
            <Label>New password</Label>
            <Input
              type="password" autoComplete="new-password"
              value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              data-testid="as-new-input"
              required
            />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input
              type="password" autoComplete="new-password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="as-confirm-input"
              required
            />
          </div>

          <PasswordRulesHint />

          {err && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="as-error">
              {err}
            </div>
          )}

          <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="as-submit-btn">
            {saving ? (<><Loader2 className="mr-2 size-4 animate-spin" /> Updating…</>) : "Change password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
